import { Box, Container, Stack, Typography } from '@mui/material'
import { FeatureFlagIdPasswordPush } from 'c1gen/features'
import { useHasBuiltinRole, useHasRequiredFeatures } from 'components/no-ui/capability'
import { YearInReviewBanner } from 'components/pages/admin/dashboard/YearInReviewBanner'
import { EndUserDashboardCard } from 'components/pages/end-user-dashboard/EndUserDashboardCard'
import { EndUserDashboardExpiringAccess } from 'components/pages/end-user-dashboard/EndUserDashboardExpiringAccess'
import { EndUserDashboardMyRequests } from 'components/pages/end-user-dashboard/EndUserDashboardMyRequests'
import { EndUserDashboardMyReviews } from 'components/pages/end-user-dashboard/EndUserDashboardMyReviews'
import { EndUserDashboardTasks } from 'components/pages/end-user-dashboard/EndUserDashboardTasks'
import { ManageAccessIllustration } from 'components/pages/end-user-dashboard/illustrations/ManageAccessIllustration'
import { RequestAccessIllustration } from 'components/pages/end-user-dashboard/illustrations/RequestAccessIllustration'
import { useMainContentFocus } from 'hooks/useMainContentFocus'
import { UserValue } from 'lib/actions/user'
import { RoleID } from 'lib/constants/system'
import { UserContext } from 'lib/context/app'
import { routes } from 'lib/routes'
import Image from 'next/image'
import { ReactElement, useContext, useRef } from 'react'
import { FormattedMessage, useIntl, IntlShape } from 'react-intl'
import { PasswordResetIllustration } from './illustrations/PasswordResetIllustration'

export const getTimeBasedGreeting = (intl: IntlShape, user?: UserValue): string => {
    const hour = new Date().getHours()

    const firstNameValue = user?.profile?.fields?.firstName?.kind.oneofKind === 'stringValue'
        ? user?.profile?.fields?.firstName.kind.stringValue
        : undefined

    const name = firstNameValue || user?.displayName || false

    const separator = name
        ? (firstNameValue ? ',' : ':')
        : ''

    if (hour < 12) {
        return intl.formatMessage({
            id: 'dashboard_greeting_morning',
            defaultMessage: 'Good morning{separator} {name}',
        }, { name, separator })
    } if (hour < 17) {
        return intl.formatMessage({
            id: 'dashboard_greeting_afternoon',
            defaultMessage: 'Good afternoon{separator} {name}',
        }, { name, separator })
    }
    return intl.formatMessage({
        id: 'dashboard_greeting_evening',
        defaultMessage: 'Good evening{separator} {name}',
    }, { name, separator })
}

export const EndUserDashboard = (): ReactElement => {
    const intl = useIntl()
    const userContext = useContext(UserContext)
    const hasPasswordPushFF = useHasRequiredFeatures([FeatureFlagIdPasswordPush])
    const isSuperAdmin = useHasBuiltinRole(RoleID.SuperAdmin)
    const timeBasedGreeting = getTimeBasedGreeting(intl, userContext?.user)
    const greetingRef = useRef<HTMLDivElement>(null)
    useMainContentFocus(greetingRef)

    const showIfEmptySx = {
        display: 'none',

        '.wrapperContainer:has(.tasksContainer:empty) &': {
            display: 'flex',
        },
    }

    const cards = <Stack direction="row" gap={5}>
        <EndUserDashboardCard
            href={routes.requests.create}
            title={intl.formatMessage({ id: 'end_user_dashboard_card_title_make_a_quick_request', defaultMessage: 'Make a quick request' })}
            subtitle={intl.formatMessage({
                id: 'end_user_dashboard_card_subtitle_make_a_quick_request',
                defaultMessage: 'I know what I want!',
            })}
            IllustrationComponent={RequestAccessIllustration}
        />
        <EndUserDashboardCard
            href={routes.requests.list}
            title={intl.formatMessage({ id: 'end_user_dashboard_card_title_browse_access', defaultMessage: 'Browse access' })}
            subtitle={intl.formatMessage({
                id: 'end_user_dashboard_card_subtitle_browse_access',
                defaultMessage: "Show me what's available.",
            })}
            IllustrationComponent={ManageAccessIllustration}
        />
        {hasPasswordPushFF &&
            <EndUserDashboardCard
                href={routes.passwordReset.index}
                title={intl.formatMessage({ id: 'end_user_dashboard_card_title_password_reset', defaultMessage: 'Password reset' })}
                subtitle={intl.formatMessage({
                    id: 'end_user_dashboard_card_subtitle_password_reset',
                    defaultMessage: 'I need to reset my password.',
                })}
                IllustrationComponent={PasswordResetIllustration}
            />}
    </Stack>

    return <Box className="wrapperContainer">
        <Box sx={{ backgroundColor: 'grey.50' }}>
            <Container sx={{ position: 'relative', zIndex: 2 }}>
                <Stack gap={2} mb={5} mt={3}>
                    <Typography
                        id="dashboard-greeting"
                        ref={greetingRef}
                        variant="h2"
                        component="div"
                        tabIndex={-1}
                        sx={{ outline: 'none' }}
                    >
                        {timeBasedGreeting}
                    </Typography>
                </Stack>

                {isSuperAdmin && <YearInReviewBanner />}

                <Stack mt={4} gap={5}>
                    {cards}

                    <Stack className="tasksContainer" gap={5}>
                        <EndUserDashboardMyRequests />
                        <EndUserDashboardExpiringAccess />
                        <EndUserDashboardTasks />
                        <EndUserDashboardMyReviews />
                    </Stack>
                </Stack>
            </Container>
            <Stack alignItems="center" justifyContent="center" mt="-80px" sx={{ position: 'relative', zIndex: 1 }}>
                <Image
                    src="/static/clouds-footer.svg"
                    alt="" // presentational images should have an empty alt
                    width={1440}
                    height={112}
                    loader={({ src }): string => src}
                    unoptimized
                />
            </Stack>
        </Box>
        <Stack alignItems="center" justifyContent="center" py={4}>
            <Container>
                <Stack sx={showIfEmptySx} gap={5} flex={1} width="100%">
                    <Stack gap={2} alignItems="center" justifyContent="center">
                        <Typography variant="h2" component="div">
                            <FormattedMessage id="end_user_dashboard_empty_title" defaultMessage="Woohoo!" />
                        </Typography>
                        <Typography component="div">
                            <FormattedMessage id="end_user_dashboard_empty_subtitle" defaultMessage="There's no work for you to do right now." />
                        </Typography>
                    </Stack>
                </Stack>
            </Container>
        </Stack>
    </Box>
}
